/**
 * The shape of a founder review, and the pure things done to it.
 *
 * A leaf module: no react-query, no supabase client, no React. The fetching
 * hooks live in `profile-signals.ts`. Keeping the shape and its pure helpers
 * separate means they can be unit-tested without dragging the whole native
 * dependency graph into the test runner — the same reason `edge-timeout.ts`
 * and `submission/link-state.ts` exist.
 */

export interface InvestorReview {
  id: string;
  rating: number;
  body: string;
  isAnonymous: boolean;
  /** Null when the review is anonymous — masked server-side, not here. */
  founderName: string | null;
  companyName: string | null;
  createdAt: string;
}

/**
 * Average rating across reviews, or null when there are none.
 *
 * Null rather than 0 so the stats tile can be omitted entirely. A 0.0-star
 * investor and an unreviewed investor are very different claims, and only one
 * of them is defamatory.
 */
export function averageRating(reviews: InvestorReview[]): number | null {
  if (reviews.length === 0) return null;
  return reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;
}

/**
 * Adapt a review to the `FounderTestimonial` shape `TestimonialCard` renders.
 *
 * The card does `founderName.split(' ')` for its avatar initials, so an
 * anonymous review — where the server correctly returns NULL for both identity
 * fields — would crash it. Anonymity is a server guarantee; this only gives
 * the UI something printable for the identity it is not allowed to know.
 *
 * `verified` is unconditionally true because it is enforced at write time: RLS
 * refuses a review unless the company has a real interaction with the investor,
 * so an unverified review cannot exist.
 */
export function reviewToTestimonial(review: InvestorReview) {
  return {
    id: review.id,
    founderName: review.founderName ?? 'Anonymous founder',
    companyName: review.companyName ?? 'Company withheld',
    rating: review.rating,
    text: review.body,
    date: review.createdAt,
    verified: true,
  };
}
